import { IconButton, Tooltip } from "@mui/material"
import {
  SafeApiComponent,
  SummaryBanner,
  SummaryChipDivider,
  SummaryInfoDisplay,
  SummarySection,
  useSetFilter,
} from "@pharmupp/p3-front-commons"
import { useState } from "react"
import { FilePlus } from "react-feather"
import { useTemplateSummaryApi } from "../../Template/useTemplateSummaryApi"
import { STATUS_COLOR } from "../../commons/colors"
import { AddSignedDocumentModal } from "./AddSignedDocumentModal/AddSignedDocumentModal"
import type { SignatoryStatus } from "./types"

const HEIGHT = "100px"

export const DocumentsSummaryBanner = ({ templateId }: { templateId: string }) => (
  <SafeApiComponent
    suspense={{
      skeletonProps: { height: HEIGHT },
    }}
    hideError={true}
  >
    <DocumentsSummaryBannerInner templateId={templateId} />
  </SafeApiComponent>
)

const DocumentsSummaryBannerInner = ({ templateId }: { templateId: string }) => {
  const { summary } = useTemplateSummaryApi({ templateId })

  return (
    <SummaryBanner
      height={HEIGHT}
      rightItems={
        <AddSignedDocumentButton
          templateName={summary.name}
          templateId={templateId}
        />
      }
    >
      <DocumentSummarySection
        title="Suivi Envoi"
        chips={[
          {
            status: undefined,
            total: summary.receiversCount,
            label: "Destinataires",
            baseColor: STATUS_COLOR.ALL,
            variant: "outlined",
          },
          {
            status: "ERROR",
            total: summary.errorCount,
            label: "Erreur",
            baseColor: STATUS_COLOR.ERROR,
            variant: "outlined",
          },
        ]}
      />
      <DocumentSummarySection
        title="Suivi Signatures"
        chips={[
          {
            status: "UNSIGNED",
            total: summary.unsignedCount,
            label: "À signer",
            variant: "outlined",
            baseColor: STATUS_COLOR.UNSIGNED,
          },
          {
            status: "REFUSED",
            total: summary.refusedCount,
            label: "Refusé",
            variant: "outlined",
            baseColor: STATUS_COLOR.REFUSED,
          },
          { divider: true },
          {
            status: "SIGNED",
            total: summary.signedCount,
            label: "Signé",
            variant: "filled",
            baseColor: STATUS_COLOR.SIGNED,
          },
        ]}
      />
    </SummaryBanner>
  )
}

const AddSignedDocumentButton = ({
  templateName,
  templateId,
}: { templateName: string; templateId: string }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <>
      <Tooltip title="Ajouter manuellement un document signé">
        <IconButton
          onClick={() => setIsModalOpen(true)}
          size="large"
          sx={{
            bgcolor: "primary.light1",
            color: "white",
            "&:hover": { bgcolor: "primary.light2" },
          }}
        >
          <FilePlus />
        </IconButton>
      </Tooltip>

      {isModalOpen && (
        <AddSignedDocumentModal
          onClose={() => setIsModalOpen(false)}
          templateId={templateId}
          templateName={templateName}
        />
      )}
    </>
  )
}

type DocumentSummaryChip = {
  status?: SignatoryStatus
  total: number
  label: string
  labelColor?: string
  baseColor: string
  variant: "filled" | "outlined"
}

const DocumentSummarySection = ({
  title,
  chips,
}: {
  title: string
  chips: (DocumentSummaryChip | { divider: true })[]
}) => {
  const setFilter = useSetFilter()

  return (
    <SummarySection title={title}>
      {chips.map((chip, index) => {
        if ("divider" in chip) {
          // biome-ignore lint/suspicious/noArrayIndexKey: divider needs unique key
          return <SummaryChipDivider key={`divider-${index}`} />
        }

        return (
          <SummaryInfoDisplay
            key={`${chip.status ?? "all"}`}
            label={chip.label}
            labelColor={chip.labelColor}
            count={chip.total}
            baseColor={chip.baseColor}
            variant={chip.variant}
            onClick={
              // For the moment, there are no REFUSED documents
              chip.status === "REFUSED"
                ? undefined
                : () =>
                    setFilter([{ filterName: "status", filterValue: chip.status }])
            }
          />
        )
      })}
    </SummarySection>
  )
}
